import React from "react";
import { ITaskManagement } from "../../models/taskManagement.model";
import { IUserRef } from "../../models/userData.model";
import Button, { ButtonType } from "../Assets/Button";
import { Check, X } from "lucide-react";
import { getUserId } from "../../utils/auth";

interface TaskManagerDateExtensionApprovalTableProps {
  tasks: ITaskManagement[];
  onTaskClick: (task: ITaskManagement) => void;
  handleDateExtensionApproval?: (
    taskId: string,
    decision: "approved" | "rejected",
    index: number
  ) => void;
}

const TaskManagerDateExtensionApprovalTable: React.FC<
  TaskManagerDateExtensionApprovalTableProps
> = ({ tasks, onTaskClick, handleDateExtensionApproval }) => {
  const userId = getUserId();

  const tasksWithRequestedEndDate = tasks.filter(
    (task) =>
      task.requestedEndDate !== null &&
      task.requestedEndDate !== undefined &&
      (task.author as unknown as IUserRef)._id === userId
  );

  if (tasksWithRequestedEndDate.length === 0) return null;

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return;
    return new Date(date).toLocaleDateString();
  };

  const getAssigneeDisplay = (task: ITaskManagement) => {
    const a = task.assignee as unknown as IUserRef;
    if (a?.username) return a.username.capitalizeWords?.() || a.username;
    if (typeof task.assignee === "string") return task.assignee;
    return "Unassigned";
  };

  return (
    <section className="mb-6">
      <div className="space-y-3">
        {tasksWithRequestedEndDate.map((task, index) => {
          const assignee = getAssigneeDisplay(task);

          return (
            <article
              key={task._id}
              className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-2 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-150"
            >
              <div className="flex items-start gap-2 min-w-0">
                <div className="flex-shrink-0 p-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>

                <div className="min-w-0">
                  {/* task name */}
                  <button
                    onClick={() => onTaskClick(task)}
                    className="text-left w-full text-sm font-medium text-gray-900 hover:text-indigo-600 truncate"
                    aria-label={`Open ${task.taskName}`}
                  >
                    {task.taskName?.capitalizeWords?.() || task.taskName}
                  </button>

                  {/* sentence */}
                  <p className="mt-1 text-xs text-gray-500">
                    <span className="font-medium text-gray-700">
                      {assignee}
                    </span>{" "}
                    requested to extend deadline from{" "}
                    <span className="px-2 py-0.5 rounded-md bg-gray-50 border border-gray-100 text-xs text-gray-700">
                      {formatDate(task.endDate)}
                    </span>{" "}
                    to{" "}
                    <span className="px-2 py-0.5 rounded-md bg-gray-50 border border-gray-100 text-xs text-gray-700">
                      {formatDate(task.requestedEndDate)}
                    </span>
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs">
                <Button
                  type={ButtonType.Extra}
                  onClick={() =>
                    handleDateExtensionApproval &&
                    handleDateExtensionApproval(task._id!, "approved", index)
                  }
                  aria-label="Approve"
                  className="flex items-center gap-1"
                >
                  <Check className="w-4 h-4 text-white" />
                  <span>Approve</span>
                </Button>

                <Button
                  type={ButtonType.Error}
                  onClick={() =>
                    handleDateExtensionApproval &&
                    handleDateExtensionApproval(task._id!, "rejected", index)
                  }
                  aria-label="Reject"
                  className="flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  <span>Reject</span>
                </Button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default TaskManagerDateExtensionApprovalTable;
